import React, { Component } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { BodyText } from './components/Core/TextComponents';
import { ThemeContext } from './components/Core/ThemeContext';
import Outline from './components/Outlines/Outline';
import SEO from './components/SEO';
import { getPerspectiveBySlug } from './data/perspectives';

const featuredPerspectives = [
    getPerspectiveBySlug('what-does-paul-mean-by-works-of-the-law'),
    getPerspectiveBySlug('what-does-paul-mean-by-justification'),
    getPerspectiveBySlug('what-is-sanctification'),
].filter(Boolean);

const helpPageStyles = {
    page: {
        minHeight: '100vh',
        padding: 'clamp(16px, 3vw, 28px)',
        background: 'linear-gradient(180deg, #f8f8f4 0%, #edf4f3 42%, #f9fbfb 100%)',
    },
    frame: { width: 'min(100%, 940px)', margin: '0 auto' },
    stack: { display: 'flex', flexDirection: 'column', gap: 18 },
    heroShell: {
        display: 'flex',
        background: 'linear-gradient(135deg, #fffdfa 0%, #f4faf9 100%)',
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid rgba(188, 198, 208, 0.4)',
        boxShadow: '0 14px 32px rgba(86, 96, 110, 0.1)',
    },
    heroStrip: { width: 18, minWidth: 18, background: 'var(--color-grad-1)' },
    heroBody: { flex: 1, padding: 'clamp(18px, 3vw, 26px)' },
    eyebrow: {
        margin: 0,
        color: 'rgba(90, 105, 108, 0.84)',
        fontSize: '0.74em',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    heroTitle: {
        margin: '6px 0 0 0',
        fontSize: 'clamp(1.9em, 4vw, 2.45em)',
        color: 'rgba(0, 0, 0, 0.82)',
        fontFamily: 'Dancing Script, Georgia',
        lineHeight: 1.1,
    },
    heroSummary: {
        margin: '12px 0 0 0',
        color: 'rgba(0, 0, 0, 0.7)',
        fontSize: '1em',
        lineHeight: 1.7,
        maxWidth: 720,
    },
    shell: {
        display: 'flex',
        background: '#fff',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 12px 28px rgba(86, 96, 110, 0.08)',
        border: '1px solid rgba(188, 198, 208, 0.38)',
    },
    strip: { width: 12, minWidth: 12, background: 'var(--color-grad-1)' },
    content: { flex: 1, minWidth: 0, background: 'linear-gradient(180deg, #fffefb 0%, #fcfdfd 100%)' },
    summaryButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '16px clamp(16px, 2.6vw, 22px)',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        textAlign: 'left',
    },
    summaryText: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
    summaryTitle: { margin: 0, color: 'rgba(0, 0, 0, 0.8)', fontSize: '1.05em', fontWeight: 700, letterSpacing: '0.01em' },
    divider: { height: 1, background: 'rgba(188, 198, 208, 0.32)', margin: '0 clamp(16px, 2.6vw, 22px)' },
    body: { padding: '16px clamp(16px, 2.6vw, 22px) 22px', color: 'rgba(0, 0, 0, 0.76)' },
    collapsedBody: { maxHeight: 0, overflow: 'hidden', paddingTop: 0, paddingBottom: 0 },
    paragraph: { margin: '0 0 14px 0', color: 'rgba(0, 0, 0, 0.76)', fontSize: '0.98em', lineHeight: 1.76 },
    form: { display: 'flex', flexDirection: 'column', gap: 16 },
    field: { display: 'flex', flexDirection: 'column', gap: 7 },
    label: { color: 'rgba(0, 0, 0, 0.7)', fontSize: '0.9em', fontWeight: 700 },
    input: {
        width: '100%',
        minHeight: 44,
        padding: '9px 11px',
        border: '1px solid rgba(126, 145, 151, 0.55)',
        borderRadius: 6,
        background: '#fff',
        color: 'rgba(0, 0, 0, 0.78)',
        font: 'inherit',
        fontSize: '1em',
        boxSizing: 'border-box',
    },
    textarea: { minHeight: 160, resize: 'vertical' },
    captcha: { overflowX: 'auto', maxWidth: '100%' },
    button: {
        width: '100%',
        minHeight: 46,
        padding: '10px 18px',
        border: 0,
        borderRadius: 6,
        background: 'var(--color-grad-1)',
        color: '#fff',
        cursor: 'pointer',
        font: 'inherit',
        fontWeight: 700,
    },
    perspectiveLead: {
        margin: '0 0 14px 0',
        color: 'rgba(0, 0, 0, 0.76)',
        fontSize: '0.98em',
        lineHeight: 1.76,
    },
    perspectiveLinks: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
    },
    perspectiveHubLink: {
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 40,
        padding: '10px 14px',
        borderRadius: 999,
        textDecoration: 'none',
        background: '#10245c',
        border: '1px solid #10245c',
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.92em',
    },
    perspectiveTopicLink: {
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 40,
        padding: '10px 14px',
        borderRadius: 999,
        textDecoration: 'none',
        background: 'linear-gradient(135deg, #fffef9, #f4f8fc)',
        border: '1px solid rgba(47, 67, 115, 0.14)',
        color: '#22345f',
        fontWeight: 600,
        fontSize: '0.92em',
    },
};

function HelpHero() {
    return (
        <div style={helpPageStyles.heroShell}>
            <div style={helpPageStyles.heroStrip} />
            <div style={helpPageStyles.heroBody}>
                <p style={helpPageStyles.eyebrow}>Learn the app</p>
                <h1 style={helpPageStyles.heroTitle}>Help</h1>
                <p style={helpPageStyles.heroSummary}>
                    Find your way around ai-BIBLE, from Bible study tools and outlines to chat and support.
                </p>
            </div>
        </div>
    );
}

function HelpSection({ title, eyebrow, children, startsOpen = true }) {
    const [open, setOpen] = React.useState(startsOpen);

    return (
        <section style={helpPageStyles.shell}>
            <div style={helpPageStyles.strip} aria-hidden="true" />
            <div style={helpPageStyles.content}>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    style={helpPageStyles.summaryButton}
                >
                    <span style={helpPageStyles.summaryText}>
                        <span style={helpPageStyles.eyebrow}>{eyebrow}</span>
                        <h2 style={helpPageStyles.summaryTitle}>{title}</h2>
                    </span>
                    <ExpandMoreIcon
                        style={{
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 140ms ease',
                            color: 'rgba(83, 102, 106, 0.85)',
                        }}
                    />
                </button>
                <div style={helpPageStyles.divider} aria-hidden="true" />
                <div aria-hidden={!open} style={{ ...helpPageStyles.body, ...(open ? null : helpPageStyles.collapsedBody) }}>
                    {children}
                </div>
            </div>
        </section>
    );
}

const outline = {
    /* the args you need here will depend on your component */
    colorIndex: 1,
    title: "Separation Through Disobedience",
    subsections: [
        {
          "osisRange": "Gen.4.1-Gen.6.22",
          "title": "Corruption of Humanity",
          "text": "Humanity descends into murder and corruption outside of God's presence, and suffers judgement.",
          "footnote": []
        },
        {
          "osisRange": "Gen.7.1-Gen.10.32",
          "title": "Noah's Obedience",
          "text": "Noah is obedient and blameless and is saved and blessed.",
          "footnote": []
        },
        {
          "osisRange": "Gen.11.1-Gen.11.32",
          "title": "Tower of Babel",
          "text": "Humanity seeks to save themselves by building a name for themselves.",
          "footnote": []
        }
      ]
};

class ContactUs extends Component {
    static contextType = ThemeContext;

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.context.loading(false);
    }

    componentWillUnmount() {
        console.log('Will Unmount');
    }

    render() {
        const title = 'ai-BIBLE Help | How to Use Search, Cross References and Chat';
        const description = 'Learn how to use ai-BIBLE search, cross references, outlines, devotionals, and chat features.';
        document.title = title;
        return (
            <div style={helpPageStyles.page}>
                <SEO canonical="https://www.ai-bible.com/help" title={title} description={description} />
                <div style={helpPageStyles.frame}>
                    <div style={helpPageStyles.stack}>
                        <HelpHero />

                        <HelpSection title="About ai-BIBLE" eyebrow="Overview">
                            <p style={helpPageStyles.paragraph}>
                                ai-BIBLE is a line-by-line Bible application built to help facilitate application and interpretation of the Bible. It uses artificial intelligence in a number of ways to present the Bible, pull together different data sources, and process natural-language questions for the chatbot.
                            </p>
                            <p style={helpPageStyles.paragraph}>
                                ai-BIBLE uses mostly public-domain data or sources where licensing has been explicitly granted. Much of the information comes from the Berean Bible, OpenBible.info, or Robert Rouse&apos;s theographic Bible information. Some summaries have been added by hand to support accuracy, with source dropdowns showing consulted sources.
                            </p>
                            <p style={{ ...helpPageStyles.paragraph, marginBottom: 0 }}>
                                Chat falls back on OpenAI&apos;s ChatGPT when ai-BIBLE does not have a specific response. These responses are reviewed periodically for correctness.
                            </p>
                        </HelpSection>

                        <HelpSection title="Outlines and cross references" eyebrow="Study tools" startsOpen={false}>
                            <BodyText>
                                An outline explains what a passage is about, and each number can be clicked to open the passage in the Bible. Outlines and groups of cross references are presented in a context-aware style.
                            </BodyText>
                            <p style={{ ...helpPageStyles.paragraph, marginTop: 16 }}>
                                {outline.title}
                            </p>
                            <Outline outline={outline.subsections} title="Cross references in context" />
                        </HelpSection>

                        <HelpSection title="Chatbot" eyebrow="Ask questions" startsOpen={false}>
                            <p style={{ ...helpPageStyles.paragraph, marginBottom: 0 }}>
                                The chatbot attempts to answer your questions using its own information. If it does not have an answer, it falls back to ChatGPT. Answers are stored and reviewed so they can be reused with corrections or amendments.
                            </p>
                        </HelpSection>

                        <HelpSection title="Interpretive perspectives" eyebrow="Compare views" startsOpen={false}>
                            <p style={helpPageStyles.perspectiveLead}>
                                The perspectives section compares major readings of debated passages and doctrines, with cited sources and linked follow-up topics.
                            </p>
                            <div style={helpPageStyles.perspectiveLinks}>
                                <a href="/perspectives" style={helpPageStyles.perspectiveHubLink}>Browse all perspectives</a>
                                {featuredPerspectives.map((topic) => (
                                    <a key={topic.slug} href={`/perspectives/${topic.slug}`} style={helpPageStyles.perspectiveTopicLink}>
                                        {topic.shortTitle}
                                    </a>
                                ))}
                            </div>
                        </HelpSection>

                        {window.web_version ? (
                            <HelpSection title="Ask for help" eyebrow="Contact form" startsOpen={false}>
                                <p style={helpPageStyles.paragraph}>
                                    If you have a question, get in touch using the form below.
                                </p>
                                <form id="ajax-contact" method="post" action="https://www.theshapeofsound.com/mailer.php" style={helpPageStyles.form}>
                                    <div style={helpPageStyles.field}>
                                        <label htmlFor="name" style={helpPageStyles.label}>Name</label>
                                        <input type="text" style={helpPageStyles.input} id="name" name="name" required />
                                    </div>
                                    <div style={helpPageStyles.field}>
                                        <label htmlFor="email" style={helpPageStyles.label}>Email</label>
                                        <input type="email" id="email" name="email" style={helpPageStyles.input} required />
                                    </div>
                                    <div style={helpPageStyles.field}>
                                        <label htmlFor="message" style={helpPageStyles.label}>Message</label>
                                        <textarea id="message" name="message" style={{ ...helpPageStyles.input, ...helpPageStyles.textarea }} required />
                                    </div>
                                    <div style={helpPageStyles.captcha}>
                                        <ReCAPTCHA sitekey="6LfrH9MUAAAAAFe13mga-k3s6sF7tzi6krOreESP" />
                                    </div>
                                    <button type="submit" style={helpPageStyles.button}>Send message</button>
                                </form>
                            </HelpSection>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }
}

export default ContactUs;
